import React, { Component } from "react";
import { Table, Tag, Space, Switch, Empty, Alert, Tooltip } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  MinusCircleOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { ModalItem, ButtonItem, dineroDisabledMask } from "../../../../Components/Items";
import {
  MOTIVO_SBC_TEXTO, SBC_TAG_COLOR, nombreEstadoSbc, esSbcDesfasado, resumirRevisionSbc,
} from "../sbc";

/**
 * Detalle de la revisión del Salario Base de Cotización del listado de empleados.
 *
 * NO consulta nada: recibe los mismos renglones que ya pintó la tabla, porque
 * `selectTodosEmpleados` calcula el estado del SBC al vuelo y lo manda en cada empleado. Así el
 * modal no puede desincronizarse de los colores de la tabla que lo abrió.
 *
 * Es un `Table` de antd y no `TableItem` a propósito, por lo mismo que ModalValidacionTimbrado:
 * TableItem no respeta `pagination` ni `scroll`, y aquí hacen falta los dos.
 *
 * Props:
 *   visible, onCancel, loading
 *   empleados      los renglones del listado, cada uno con `sbcRevision`
 *   onCorregir     (empleadoIds) => void   opcional; sin esto el modal sólo informa
 */

const ICONO_ESTADO = {
  menor: <ArrowDownOutlined />,
  mayor: <ArrowUpOutlined />,
  noEvaluable: <MinusCircleOutlined />,
};

const EtiquetaSbc = ({ estado }) => (
  <Tag color={SBC_TAG_COLOR[estado] || "default"} icon={ICONO_ESTADO[estado]}>
    {nombreEstadoSbc(estado)}
  </Tag>
);

class ModalSbcDesactualizado extends Component {
  state = {
    verNoEvaluables: false,
    seleccionados: null,   // null = todavía no se toca la selección; se premarcan los `menor`
  };

  componentDidUpdate(prevProps) {
    // Al reabrirse, la selección vuelve a los `menor` de la lista actual: un id que ya se corrigió
    // seguiría marcado y el botón diría que va a corregir gente que ya está bien.
    if (this.props.visible && !prevProps.visible) {
      this.setState({ seleccionados: null, verNoEvaluables: false });
    }
  }

  /** Los `menor` van premarcados; los `mayor` no, porque suelen ser un SBC negociado a propósito. */
  seleccionEfectiva(filas) {
    if (this.state.seleccionados !== null) return this.state.seleccionados;
    return filas.filter((e) => e.sbcRevision?.estado === "menor").map((e) => e._id);
  }

  render() {
    const { visible, onCancel, loading, empleados = [], onCorregir } = this.props;
    const { verNoEvaluables } = this.state;

    const revisados = empleados.filter((e) => e.sbcRevision);
    const desfasados = revisados.filter((e) => esSbcDesfasado(e.sbcRevision.estado));
    const noEvaluables = revisados.filter((e) => e.sbcRevision.estado === "noEvaluable");
    const resumen = resumirRevisionSbc(revisados);

    const filas = verNoEvaluables ? [...desfasados, ...noEvaluables] : desfasados;
    const seleccionados = this.seleccionEfectiva(filas);
    // Sólo se puede corregir lo que tiene un valor calculado: un "no evaluable" no lo tiene.
    const corregibles = seleccionados.filter((id) =>
      filas.some((e) => e._id === id && esSbcDesfasado(e.sbcRevision.estado))
    );

    const columnas = [
      {
        title: "Empleado",
        dataIndex: "nombre",
        key: "nombre",
        render: (nombre, r) => (
          <div>
            <div>{nombre || "(sin nombre)"}</div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              {r.codigo !== null && r.codigo !== undefined ? `No. ${r.codigoTexto || r.codigo}` : r.serieNumero}
              {r.puesto ? ` · ${r.puesto}` : ""}
            </div>
          </div>
        ),
      },
      {
        title: "Antig.",
        key: "anios",
        width: 70,
        align: "center",
        render: (_, r) => (r.sbcRevision.anios != null ? `${r.sbcRevision.anios} año(s)` : "—"),
      },
      {
        title: "Factor",
        key: "factor",
        width: 90,
        align: "right",
        render: (_, r) => (r.sbcRevision.factor != null ? r.sbcRevision.factor : "—"),
      },
      {
        title: "Salario diario",
        dataIndex: "salarioDiario",
        key: "salarioDiario",
        width: 120,
        align: "right",
        render: (v) => (v ? dineroDisabledMask(v) : "—"),
      },
      {
        title: "SBC capturado",
        key: "actual",
        width: 130,
        align: "right",
        render: (_, r) => dineroDisabledMask(r.sbcRevision.actual || 0),
      },
      {
        title: "SBC calculado",
        key: "esperado",
        width: 130,
        align: "right",
        render: (_, r) =>
          r.sbcRevision.esperado != null
            ? <strong>{dineroDisabledMask(r.sbcRevision.esperado)}</strong>
            : "—",
      },
      {
        title: "Diferencia",
        key: "diferencia",
        width: 120,
        align: "right",
        sorter: (a, b) => (a.sbcRevision.diferencia ?? 0) - (b.sbcRevision.diferencia ?? 0),
        render: (_, r) => {
          const d = r.sbcRevision.diferencia;
          if (d == null) return "—";
          return (
            <span style={{ color: d < 0 ? "#CF1322" : "#C9A227" }}>
              {d > 0 ? "+" : ""}{dineroDisabledMask(d)}
            </span>
          );
        },
      },
      {
        title: "Estado",
        key: "estado",
        width: 190,
        render: (_, r) => {
          const { estado, motivo } = r.sbcRevision;
          if (estado === "noEvaluable") {
            return (
              <Tooltip title={MOTIVO_SBC_TEXTO[motivo] || ""}>
                <span><EtiquetaSbc estado={estado} /></span>
              </Tooltip>
            );
          }
          return <EtiquetaSbc estado={estado} />;
        },
      },
    ];

    const hayAlgo = filas.length > 0;

    return (
      <ModalItem
        title="Revisión del Salario Base de Cotización"
        visible={visible}
        onCancel={onCancel}
        spinning={loading}
      >
        <div style={{ paddingTop: 10 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="El SBC se compara contra el salario diario por el factor de integración de la tabla de prestaciones del empleado, según su antigüedad."
            description="Un SBC menor al calculado es el que hay que atender: subdeclarar ante el IMSS. Uno mayor suele ser un salario integrado pactado a propósito, por eso no viene premarcado."
          />

          <div style={{ marginBottom: 10, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <Space wrap>
              <Tag color="error" icon={<ArrowDownOutlined />}>{resumen.menor} menor(es) al calculado</Tag>
              <Tag color="warning" icon={<ArrowUpOutlined />}>{resumen.mayor} mayor(es)</Tag>
              <Tag color="success">{resumen.ok} correcto(s)</Tag>
            </Space>
            {noEvaluables.length > 0 && (
              <Space size="small">
                <Switch
                  size="small"
                  checked={verNoEvaluables}
                  onChange={(v) => this.setState({ verNoEvaluables: v, seleccionados: null })}
                />
                <span style={{ fontSize: 12 }}>
                  Ver los {noEvaluables.length} que no se pudieron evaluar
                </span>
              </Space>
            )}
          </div>

          {!hayAlgo && !loading && (
            <Empty
              description="Todos los empleados activos tienen el SBC al día."
              style={{ margin: "20px 0" }}
            />
          )}

          {hayAlgo && (
            <div style={{ overflowX: "auto" }}>
              <Table
                size="small"
                rowKey={(r) => String(r._id)}
                dataSource={filas}
                columns={columnas}
                pagination={{ pageSize: 25, showSizeChanger: false }}
                scroll={{ x: 1100, y: 420 }}
                rowSelection={onCorregir ? {
                  selectedRowKeys: seleccionados,
                  onChange: (keys) => this.setState({ seleccionados: keys }),
                  getCheckboxProps: (r) => ({ disabled: !esSbcDesfasado(r.sbcRevision.estado) }),
                } : undefined}
              />
            </div>
          )}

          {onCorregir && (
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <ButtonItem
                type="primary"
                olivo
                icon={<ToolOutlined />}
                disabled={!corregibles.length || loading}
                onClick={() => onCorregir(corregibles)}
              >
                {corregibles.length === 1
                  ? "Corregir 1 empleado"
                  : `Corregir ${corregibles.length} empleados`}
              </ButtonItem>
            </div>
          )}
        </div>
      </ModalItem>
    );
  }
}

export default ModalSbcDesactualizado;
